// AuthLeftPanel.jsx — shared left panel for SignIn / SignUp.
// Mirrors HomePage's "pipeline" tabbed section: four pills (scan → optimize →
// apply → interview) auto-cycle every 5s and reveal a compact animated preview.
import { useState, useEffect, useRef, Fragment } from "react"
import { Link } from "react-router-dom"
import resolutLogo from "../assets/resolut-logo.png"

const FF = '"Plus Jakarta Sans", "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif'
const AUTO_MS = 5000

// Palette (aligned with HomePage pipeline)
const OBSIDIAN = "#121212"
const EGGSHELL = "#F5F0E6"
const EMBER    = "#D95F19"
const CHALK    = "#e4e4e7"
const GRAVEL   = "#71717a"
const INK      = "#18181b"
const SUBTLE   = "#a1a1aa"
const BG       = "#F5F5F5"
const CARD     = "#FFFFFF"
const SOFT     = "#FAFAFA"
const MINT     = "#166534"
const MINTBG   = "#ECFDF3"
const MINTBD   = "#C7EDD4"
const EMBERBG  = "#FFF5EC"
const EMBERBD  = "#F7D9BE"

const TABS = [
  { n: "01", l: "scan" },
  { n: "02", l: "optimize" },
  { n: "03", l: "apply" },
  { n: "04", l: "interview" },
]

/* ──────────────────────────────────────────────────────────────
   Shared stage wrappers
   ────────────────────────────────────────────────────────────── */
function StageCard({ children }) {
  return (
    <div className="apl-stage" style={{
      background: CARD,
      border: `1px solid ${CHALK}`,
      borderRadius: 14,
      padding: 14,
      minHeight: 220,
      boxShadow: "0 1px 2px rgba(24,24,27,0.04), 0 24px 48px -22px rgba(24,24,27,0.14)",
    }}>
      {children}
    </div>
  )
}

function StageHeader({ title, aside }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      marginBottom: 10, paddingBottom: 10,
      borderBottom: `1px dashed ${CHALK}`,
    }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: INK, letterSpacing: "-0.01em" }}>{title}</div>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: GRAVEL, letterSpacing: "0.04em" }}>{aside}</div>
    </div>
  )
}

/* ── 01 · Scan ───────────────────────────────────────────────── */
function StageScan() {
  const LINES = [
    { t: "13:56:22", u: "notion.so/careers",     r: "0 new" },
    { t: "13:56:21", u: "ramp.com/careers",      r: "0 new" },
    { t: "13:56:20", u: "datadoghq.com/careers", r: "+2 new" },
    { t: "13:56:19", u: "airtable.com/careers",  r: "0 new" },
    { t: "13:56:18", u: "stripe.com/jobs",       r: "+1 match" },
  ]
  return (
    <StageCard>
      <StageHeader title="Scanning career pages" aside="50,000+ sources" />
      <div style={{
        display: "flex", flexDirection: "column", gap: 4,
        padding: "9px 10px",
        background: SOFT,
        border: `1px solid ${CHALK}`,
        borderRadius: 10,
      }}>
        {LINES.map((row, i) => {
          const match = row.r.startsWith("+")
          return (
            <div
              key={i}
              className="apl-logline"
              style={{
                animationDelay: `${i * 90}ms`,
                display: "flex", gap: 8, alignItems: "center",
                fontSize: 11, lineHeight: 1.35,
                fontFamily: '"SF Mono", Menlo, monospace',
                color: GRAVEL,
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ color: SUBTLE }}>[{row.t}]</span>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>scanning {row.u}</span>
              <span style={{ color: match ? EMBER : SUBTLE, fontWeight: 600 }}>{row.r}</span>
            </div>
          )
        })}
      </div>

      <div className="apl-hit" style={{
        marginTop: 10,
        display: "flex", alignItems: "center", gap: 8,
        padding: "9px 11px",
        background: EMBERBG,
        border: `1px solid ${EMBERBD}`,
        borderRadius: 10,
      }}>
        <span className="apl-pulse-dot" style={{ width: 6, height: 6, borderRadius: "50%", background: EMBER }} />
        <span style={{ fontSize: 12.5, fontWeight: 600, color: INK, flex: 1, letterSpacing: "-0.01em" }}>
          Senior Frontend · Stripe
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, color: EMBER, letterSpacing: "0.08em" }}>MATCH 92%</span>
      </div>
    </StageCard>
  )
}

/* ── 02 · Optimize ───────────────────────────────────────────── */
function StageOptimize() {
  const KW = ["React", "TypeScript", "A11y", "SSR"]
  return (
    <StageCard>
      <StageHeader title="Tailoring your résumé" aside="ATS-safe" />
      <div style={{ display: "flex", gap: 12, alignItems: "stretch" }}>
        {/* Mini résumé thumbnail with ember edits */}
        <div style={{
          width: 76, borderRadius: 6,
          background: "linear-gradient(180deg, #fff, #FAFAFA)",
          border: `1px solid ${CHALK}`,
          padding: 8,
          display: "flex", flexDirection: "column", gap: 3,
          flexShrink: 0,
          position: "relative",
        }}>
          <div style={{ height: 4, background: INK, borderRadius: 1, width: "70%" }} />
          <div style={{ height: 1.5, background: "#D4D4D8", borderRadius: 1 }} />
          <div style={{ height: 1.5, background: "#D4D4D8", borderRadius: 1 }} />
          <div style={{ height: 1.5, background: "#D4D4D8", borderRadius: 1, width: "60%" }} />
          <div style={{ height: 1, background: CHALK, margin: "3px 0" }} />
          <div style={{ height: 1.5, background: "#D4D4D8", borderRadius: 1 }} />
          <div className="apl-edit" style={{ animationDelay: "500ms", height: 1.5, background: EMBER, borderRadius: 1, width: "55%" }} />
          <div style={{ height: 1.5, background: "#D4D4D8", borderRadius: 1 }} />
          <div className="apl-edit" style={{ animationDelay: "1200ms", height: 1.5, background: EMBER, borderRadius: 1, width: "80%" }} />
          <div style={{ height: 1.5, background: "#D4D4D8", borderRadius: 1 }} />
          <div className="apl-edit" style={{ animationDelay: "1900ms", height: 1.5, background: EMBER, borderRadius: 1, width: "40%" }} />
        </div>

        {/* Keyword chips */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: GRAVEL, letterSpacing: "0.08em" }}>KEYWORDS MATCHED</div>
          {KW.map((w, i) => (
            <div key={w} className="apl-kw" style={{
              animationDelay: `${350 + i * 320}ms`,
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "4px 9px",
              background: MINTBG, border: `1px solid ${MINTBD}`, borderRadius: 100,
              fontSize: 11.5, fontWeight: 600, color: MINT,
            }}>
              <svg width="10" height="10" viewBox="0 0 20 20" fill="none">
                <path d="M4 10l5 5 7-7" stroke={MINT} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {w}
            </div>
          ))}
        </div>
      </div>

      {/* ATS score bar */}
      <div style={{
        marginTop: 10, padding: "9px 11px",
        background: SOFT, border: `1px solid ${CHALK}`, borderRadius: 10,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, marginBottom: 6 }}>
          <span style={{ fontWeight: 600, color: GRAVEL }}>ATS match score</span>
          <span className="apl-score" style={{ color: INK, fontWeight: 800, letterSpacing: "-0.01em", fontSize: 12 }}>94%</span>
        </div>
        <div style={{ height: 6, background: CHALK, borderRadius: 999, overflow: "hidden" }}>
          <div className="apl-bar" style={{ height: "100%", width: "94%", background: `linear-gradient(90deg, ${EMBER}, #F8A75A)`, borderRadius: 999 }} />
        </div>
      </div>
    </StageCard>
  )
}

/* ── 03 · Apply ──────────────────────────────────────────────── */
function StageApply() {
  const FIELDS = [
    { l: "Full name",     v: "Jane Smith" },
    { l: "Email",         v: "jane@…" },
    { l: "Résumé",        v: "resume_stripe.pdf" },
    { l: "Cover letter",  v: "generated · 180 words" },
  ]
  return (
    <StageCard>
      <StageHeader title="Submitting application" aside="Workday · ATS" />

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {FIELDS.map((f, i) => (
          <div key={f.l} className="apl-field" style={{
            animationDelay: `${150 + i * 350}ms`,
            display: "flex", alignItems: "center", gap: 10,
            padding: "7px 11px",
            background: CARD, border: `1px solid ${CHALK}`, borderRadius: 8,
          }}>
            <div style={{
              width: 18, height: 18, borderRadius: "50%",
              background: MINTBG, border: `1px solid ${MINTBD}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <svg width="10" height="10" viewBox="0 0 20 20" fill="none">
                <path d="M4 10l5 5 7-7" stroke={MINT} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: INK, width: 86, flexShrink: 0 }}>{f.l}</span>
            <span style={{
              flex: 1, minWidth: 0, fontSize: 11.5, color: GRAVEL,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>{f.v}</span>
          </div>
        ))}
      </div>

      <div className="apl-submit" style={{
        marginTop: 10,
        display: "flex", alignItems: "center", gap: 8,
        padding: "10px 12px",
        background: INK, color: "#fff", borderRadius: 10,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M4 12l6 6L20 6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>Submitted to Stripe</span>
        <span style={{
          marginLeft: "auto", fontSize: 10, fontWeight: 700,
          color: "rgba(255,255,255,0.6)", letterSpacing: "0.08em",
        }}>00:03s</span>
      </div>
    </StageCard>
  )
}

/* ── 04 · Interview ──────────────────────────────────────────── */
function StageInterview() {
  return (
    <StageCard>
      <StageHeader title="Interview scheduled" aside="You're on." />

      <div className="apl-ivcard" style={{
        padding: 12,
        background: "linear-gradient(180deg, #FFF5EC, #FFFFFF)",
        border: `1px solid ${EMBERBD}`,
        borderRadius: 12,
        display: "flex", gap: 12, alignItems: "center",
      }}>
        {/* Calendar date tile */}
        <div style={{
          width: 54, height: 60, borderRadius: 10,
          background: "#fff", border: `1px solid ${CHALK}`,
          display: "flex", flexDirection: "column", alignItems: "center",
          overflow: "hidden", flexShrink: 0,
          boxShadow: "0 1px 2px rgba(24,24,27,0.04)",
        }}>
          <div style={{
            height: 16, background: EMBER, color: "#fff", width: "100%",
            fontSize: 9, fontWeight: 800, letterSpacing: "0.1em",
            textAlign: "center", lineHeight: "16px",
          }}>THU</div>
          <div style={{
            flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22, fontWeight: 700, color: INK, letterSpacing: "-0.02em",
          }}>12</div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: INK, letterSpacing: "-0.01em" }}>
            Stripe · Technical screen
          </div>
          <div style={{ fontSize: 11.5, color: GRAVEL, marginTop: 2 }}>
            Dec 12 · 10:30 am IST · 45 min
          </div>
          <div style={{ marginTop: 6, display: "flex", gap: 6 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, color: MINT,
              background: MINTBG, border: `1px solid ${MINTBD}`,
              padding: "2px 7px", borderRadius: 100, letterSpacing: "0.06em",
            }}>CONFIRMED</span>
          </div>
        </div>
      </div>

      <div className="apl-inbox" style={{
        marginTop: 10,
        display: "flex", alignItems: "center", gap: 10,
        padding: "8px 11px",
        background: SOFT, border: `1px solid ${CHALK}`, borderRadius: 10,
      }}>
        <div style={{
          width: 24, height: 24, borderRadius: "50%",
          background: "#F5F0FF", border: `1px solid ${CHALK}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 10, fontWeight: 800, color: "#6D28D9", flexShrink: 0,
        }}>P</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 11.5, fontWeight: 600, color: INK,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>Priya · Stripe recruiting</div>
          <div style={{
            fontSize: 11, color: GRAVEL, marginTop: 1,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>Great to meet you Thursday — calendar attached.</div>
        </div>
      </div>
    </StageCard>
  )
}

const STAGES = [StageScan, StageOptimize, StageApply, StageInterview]

/* ══════════════════════════════════════════════════════════════
   MAIN
   ══════════════════════════════════════════════════════════════ */
export default function AuthLeftPanel() {
  const [active, setActive] = useState(0)
  const paused = useRef(false)

  useEffect(() => {
    const id = setInterval(() => {
      if (!paused.current) setActive((v) => (v + 1) % STAGES.length)
    }, AUTO_MS)
    return () => clearInterval(id)
  }, [])

  const StageComp = STAGES[active]

  return (
    <div
      onMouseEnter={() => { paused.current = true }}
      onMouseLeave={() => { paused.current = false }}
      style={{
        flex: 1,
        position: "relative",
        background: BG,
        borderRight: `1px solid ${CHALK}`,
        padding: "40px 56px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: "100vh",
        fontFamily: FF,
      }}
    >
      <style>{`
        @keyframes apl-pulse  { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:.45;transform:scale(1.5);} }
        @keyframes apl-fade   { from{opacity:0;transform:translateY(4px);} to{opacity:1;transform:translateY(0);} }
        @keyframes apl-fadeIn { from{opacity:0;} to{opacity:1;} }
        @keyframes apl-bar    { from{width:0;} to{width:94%;} }
        .apl-pulse-dot { animation: apl-pulse 1.6s ease-in-out infinite; transform-origin: center; }
        .apl-stage     { animation: apl-fade 420ms both; }
        .apl-logline   { animation: apl-fade 320ms both; opacity: 0; }
        .apl-hit       { animation: apl-fade 360ms 650ms both; opacity: 0; }
        .apl-edit      { animation: apl-fadeIn 240ms both; opacity: 0; }
        .apl-kw        { animation: apl-fade 320ms both; opacity: 0; }
        .apl-field     { animation: apl-fade 300ms both; opacity: 0; }
        .apl-submit    { animation: apl-fade 360ms 1700ms both; opacity: 0; }
        .apl-bar       { animation: apl-bar 1600ms 400ms cubic-bezier(.4,0,.2,1) both; }
        .apl-ivcard    { animation: apl-fade 420ms both; }
        .apl-inbox     { animation: apl-fade 360ms 450ms both; opacity: 0; }
        .apl-grid-bg {
          background-image:
            linear-gradient(${CHALK} 1px, transparent 1px),
            linear-gradient(90deg, ${CHALK} 1px, transparent 1px);
          background-size: 44px 44px;
          mask-image: radial-gradient(ellipse 70% 60% at center, rgba(0,0,0,.55), rgba(0,0,0,0) 85%);
          -webkit-mask-image: radial-gradient(ellipse 70% 60% at center, rgba(0,0,0,.55), rgba(0,0,0,0) 85%);
        }
        .apl-glow-ember {
          position: absolute; width: 460px; height: 460px; border-radius: 50%;
          background: radial-gradient(circle, rgba(217,95,25,0.14), transparent 65%);
          filter: blur(40px); pointer-events: none;
        }
      `}</style>

      {/* Decorations */}
      <div className="apl-grid-bg" style={{ position: "absolute", inset: 0, opacity: 0.5 }} />
      <div className="apl-glow-ember" style={{ top: -120, right: -80 }} />

      {/* Logo */}
      <Link to="/" style={{
        position: "relative", zIndex: 1,
        display: "flex", alignItems: "center", gap: 10,
        textDecoration: "none", alignSelf: "flex-start",
      }}>
        <img src={resolutLogo} alt="Resolut" style={{ width: 32, height: 32, borderRadius: 7, objectFit: "contain" }} />
        <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.025em", color: INK }}>
          Resolut
        </span>
      </Link>

      {/* Content */}
      <div style={{
        position: "relative", zIndex: 1,
        flex: 1,
        display: "flex", flexDirection: "column", justifyContent: "center",
        maxWidth: 500,
      }}>
        {/* Eyebrow */}
        <div style={{
          fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
          color: EMBER, marginBottom: 12, fontWeight: 700,
        }}>
          The pipeline.
        </div>

        {/* Headline */}
        <h2 style={{
          margin: "0 0 22px",
          fontSize: 32, lineHeight: 1.12,
          fontWeight: 700, letterSpacing: "-0.03em",
          color: INK,
        }}>
          From job post to interview invite.
        </h2>

        {/* Tab bar */}
        <div role="tablist" style={{
          display: "flex", alignItems: "center",
          width: "100%", marginBottom: 18,
        }}>
          {TABS.map((tab, i) => (
            <Fragment key={tab.n}>
              <button
                role="tab"
                aria-selected={active === i}
                onClick={() => setActive(i)}
                style={{
                  appearance: "none", cursor: "pointer", flexShrink: 0,
                  background: active === i ? OBSIDIAN : "transparent",
                  color: active === i ? EGGSHELL : GRAVEL,
                  border: `1px solid ${active === i ? OBSIDIAN : CHALK}`,
                  borderRadius: 999,
                  padding: "7px 12px",
                  display: "inline-flex", alignItems: "center", gap: 7,
                  fontFamily: FF,
                  transition: "background 320ms ease, color 320ms ease, border-color 320ms ease",
                }}
              >
                <span style={{
                  fontSize: 10, letterSpacing: "0.08em",
                  fontVariantNumeric: "tabular-nums",
                  opacity: active === i ? 0.65 : 0.55,
                }}>{tab.n}</span>
                <span style={{
                  fontSize: 12.5, fontWeight: 500,
                  textTransform: "capitalize", letterSpacing: "-0.005em",
                }}>{tab.l}</span>
              </button>
              {i < TABS.length - 1 && (
                <div style={{ flex: 1, height: 1, background: CHALK, margin: "0 8px" }} />
              )}
            </Fragment>
          ))}
        </div>

        {/* Stage preview — key forces remount so animations restart */}
        <StageComp key={active} />

        {/* Caption */}
        <p style={{ margin: "16px 0 0", fontSize: 13, color: GRAVEL, lineHeight: 1.5 }}>
          ~1 minute a day on the platform. First interviews in as little as{" "}
          <strong style={{ color: INK, fontWeight: 700 }}>24 hours</strong>.
        </p>
      </div>

      {/* Footer */}
      <div style={{
        position: "relative", zIndex: 1,
        fontSize: 12, color: GRAVEL,
      }}>
        © {new Date().getFullYear()} Resolut ·{" "}
        <a href="#" style={{ color: GRAVEL, textDecoration: "none" }}>Terms</a> ·{" "}
        <a href="#" style={{ color: GRAVEL, textDecoration: "none" }}>Privacy</a>
      </div>
    </div>
  )
}
